// `styleRules` — a block's style that depends on its data: a late ticket goes
// red, a selected card gets an outline, an occupied table turns amber. Each rule
// whose `when` holds layers its `style` over the block's own, in order. The
// condition language is visibility's (`evalCondition`), so an author learns one
// syntax, and inside a repeat it reads the row like any other token.
//
// Every surface calls `applyStyleRules` where it already evaluates visibility,
// then hands the resulting `style` to the shared `StyledBlock` — one rule
// engine, so the Run view, a downloaded app and the static site cannot disagree.

import type { BlockStyle, BlockStyleRule } from '@twasta/page-schema'
import { substituteVars, type VarSource } from '../actions/substitute'
import { evalCondition } from '../visibility'
import { readsAge } from './time'

/** Merge two styles: scalars replace, nested objects (padding, border,
 *  typography, hover, …) merge one level so a rule can change one side. */
export function mergeStyle(base: BlockStyle | undefined, over: BlockStyle | undefined): BlockStyle | undefined {
  if (!over) return base
  if (!base) return { ...over }
  const out: Record<string, unknown> = { ...base }
  for (const [k, v] of Object.entries(over)) {
    const prev = out[k]
    out[k] = v && typeof v === 'object' && !Array.isArray(v) && prev && typeof prev === 'object' && !Array.isArray(prev)
      ? { ...(prev as object), ...(v as object) }
      : v
  }
  return out as BlockStyle
}

/** The style a block wears given its rules and the current variables. */
export function resolveRuleStyle(
  style: BlockStyle | undefined,
  rules: BlockStyleRule[] | undefined,
  vars: VarSource,
): BlockStyle | undefined {
  if (!Array.isArray(rules) || !rules.length) return style
  let out = style
  for (const r of rules) {
    if (!r || !r.style) continue
    // A rule that cannot be evaluated is not applied: unlike visibility (which
    // fails OPEN so content is never hidden by a typo), a style that fails
    // closed just leaves the block looking normal.
    if (holds(r, vars)) out = mergeStyle(out, r.style)
  }
  return out
}

/** `applyStyleRules(block, vars)` → the block with its effective `style` and
 *  any `set` settings of the rules that hold. Returns the SAME object when
 *  nothing changes, so memoised renders stay put. */
export function applyStyleRules<T extends { style?: BlockStyle; styleRules?: BlockStyleRule[] }>(
  block: T, vars: VarSource,
): T {
  if (!block || !Array.isArray(block.styleRules) || !block.styleRules.length) return block
  const style = resolveRuleStyle(block.style, block.styleRules, vars)
  let out: T = style === block.style ? block : { ...block, style }
  // A `set` writes INTO the block's settings — which a repeat's copies share
  // with their template (`{...template, id}` is shallow) — so it always works
  // on a DEEP copy of its own, never on the shallow one the style made.
  let deep = false
  for (const r of block.styleRules) {
    if (!r || !r.set || typeof r.set !== 'object' || !Object.keys(r.set).length) continue
    if (!holds(r, vars)) continue
    if (!deep) { out = { ...structuredCopy(block), style: out.style }; deep = true }
    for (const [path, value] of Object.entries(r.set)) {
      if (!path || path === 'styleRules' || path.startsWith('repeat')) continue   // never rewrite the rules themselves
      writePath(out as unknown as Record<string, unknown>, path,
        typeof value === 'string' ? substituteVars(value, vars) : value)
    }
  }
  return out
}

function holds(r: BlockStyleRule, vars: VarSource): boolean {
  if (typeof r.when !== 'string' || !r.when.trim()) return false
  try { return strictCondition(r.when, vars) } catch { return false }
}

function structuredCopy<T>(v: T): T {
  try { return JSON.parse(JSON.stringify(v)) as T } catch { return { ...(v as object) } as T }
}

function writePath(root: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.')
  let cur: Record<string, unknown> = root
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i]
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = {}
    cur = cur[k] as Record<string, unknown>
  }
  cur[parts[parts.length - 1]] = value
}

/** True when any rule reads an age token — its block needs a ticking clock. */
export function rulesNeedClock(rules: BlockStyleRule[] | undefined): boolean {
  return Array.isArray(rules) && rules.some((r) => readsAge(r?.when))
}

// evalCondition is fail-OPEN (an unresolved token reads as "show it"). For a
// style that would paint every row red the moment a column is misspelt, so an
// unresolved token here means "rule does not apply".
function strictCondition(expr: string, vars: VarSource): boolean {
  for (const token of expr.match(/\{\{[^{}]*\}\}/g) || []) {
    if (substituteVars(token, vars) === token) return false
  }
  return evalCondition(expr, vars)
}
